import { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Tab, Tooltip, useTheme } from "@mui/material";
import { TabContext, TabList } from "@mui/lab";
import useStyles from "./styles";
import api from "../../services/api";
import AbaTemplates from "./abaTemplates";
import AbaScripts from "./abaScripts";

export default function Versionamento() {
	const [saudeColeta, setSaudeColeta] = useState([]);
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [abaAtiva, setAbaAtiva] = useState(0);
	const [linhaSelecionada, setLinhaSelecionada] = useState(null);
	const [coletando, setColetando] = useState(false);
	const [filtroCliente, setFiltroCliente] = useState("");
	const [filtroStatus, setFiltroStatus] = useState("");
	// Estado da aba Scripts, isolado do de Templates acima - trocar de aba nao pode perder o filtro/selecao da outra
	const [saudeColetaScripts, setSaudeColetaScripts] = useState([]);
	const [scripts, setScripts] = useState([]);
	const [loadingScripts, setLoadingScripts] = useState(true);
	const [linhaSelecionadaScripts, setLinhaSelecionadaScripts] = useState(null);
	const [filtroClienteScripts, setFiltroClienteScripts] = useState("");
	const [filtroStatusScripts, setFiltroStatusScripts] = useState("");
	// Erro isolado por aba - um unico estado compartilhado deixava o sucesso de uma busca apagar o
	// erro real que a outra acabou de setar, e o throw derrubava as duas abas mesmo com so 1 falhando
	const [erroCarregamentoTemplates, setErroCarregamentoTemplates] = useState(null);
	const [erroCarregamentoScripts, setErroCarregamentoScripts] = useState(null);
	const theme = useTheme();
	const styles = useStyles(theme);

	// Busca os dois comparativos assim que a tela monta - chamadas independentes, uma nao espera a outra
	useEffect(() => {
		buscarComparativo();
		buscarComparativoScripts();
	}, []);

	// Busca o comparativo de templates (referencia + clientes + saude da coleta)
	async function buscarComparativo() {
		setLoading(true);
		try {
			const response = await api.get("/versionamento/templates");
			const templatesAtualizados = response?.data?.templates || [];
			setSaudeColeta(response?.data?.saudeColeta || []);
			setTemplates(templatesAtualizados);
			// Se havia uma linha expandida (ex. apos "Forcar atualizacao"), atualiza com os dados novos
			// (versoes/status pos-atualizacao) - sem isso o painel expandido ficaria com dado antigo
			setLinhaSelecionada((atual) => {
				if (!atual?.chave) return atual;
				const atualizado = templatesAtualizados.find(
					(template) => template?.chave === atual.chave,
				);
				return atualizado ? { ...atualizado, id: atualizado?.chave } : atual;
			});
			setErroCarregamentoTemplates(null);
		} catch (error) {
			// Guarda o erro no state em vez de esconder atras do estado vazio "sem clientes" -
			// e relanca durante o render, pro ErrorBoundary da rota (App.js) pegar de verdade
			setErroCarregamentoTemplates(error);
		} finally {
			setLoading(false);
		}
	}

	// Busca o comparativo de scripts (referencia + clientes + saude da coleta) - mesmo padrao de buscarComparativo
	async function buscarComparativoScripts() {
		setLoadingScripts(true);
		try {
			const response = await api.get("/versionamento/scripts");
			const scriptsAtualizados = response?.data?.scripts || [];
			setSaudeColetaScripts(response?.data?.saudeColeta || []);
			setScripts(scriptsAtualizados);
			// Mesmo reaproveitamento de linha expandida que buscarComparativo faz pra templates
			setLinhaSelecionadaScripts((atual) => {
				if (!atual?.chave) return atual;
				const atualizado = scriptsAtualizados.find(
					(script) => script?.chave === atual.chave,
				);
				return atualizado ? { ...atualizado, id: atualizado?.chave } : atual;
			});
			setErroCarregamentoScripts(null);
		} catch (error) {
			// Mesmo tratamento de erro de buscarComparativo, mas em estado proprio desta aba
			setErroCarregamentoScripts(error);
		} finally {
			setLoadingScripts(false);
		}
	}

	// Dispara a coleta da aba ativa e recarrega so o comparativo dessa aba ao final
	async function handleAtualizarCache() {
		setColetando(true);
		try {
			if (abaAtiva === 0) {
				await api.post("/versionamento/templates/coletar", {});
				await buscarComparativo();
			} else {
				await api.post("/versionamento/scripts/coletar", {});
				await buscarComparativoScripts();
			}
		} catch (error) {
			// Falha silenciosa aqui - o painel de saude da coleta ja mostra o motivo por cliente
		} finally {
			setColetando(false);
		}
	}

	// Relanca durante o render so o erro da aba ativa - e assim que um ErrorBoundary de React consegue
	// capturar um erro que na verdade veio de uma chamada assincrona (GET falhou). Erro da aba que o
	// usuario nao esta vendo fica guardado no state, sem derrubar a tela inteira
	if (abaAtiva === 0 && erroCarregamentoTemplates) {
		throw erroCarregamentoTemplates;
	}
	if (abaAtiva === 1 && erroCarregamentoScripts) {
		throw erroCarregamentoScripts;
	}

	return (
		<Box sx={styles.container}>
			<Box sx={styles.cabecalho}>
				<TabContext value={abaAtiva}>
					<TabList>
						<Tab label="Templates" value={0} onClick={() => setAbaAtiva(0)} />
						<Tab label="Scripts" value={1} onClick={() => setAbaAtiva(1)} />
					</TabList>
				</TabContext>
				{coletando ? (
					<Tooltip title="Uma coleta já está em andamento">
						<span>
							<Button
								variant="contained"
								disabled
								sx={styles.botaoAtualizar}
							>
								<CircularProgress size={24} />
							</Button>
						</span>
					</Tooltip>
				) : (
					<Button
						variant="contained"
						sx={styles.botaoAtualizar}
						onClick={handleAtualizarCache}
					>
						Atualizar cache
					</Button>
				)}
			</Box>
			<Box sx={styles.contentContainer}>
				{abaAtiva === 0 && (
					<AbaTemplates
						templates={templates}
						saudeColeta={saudeColeta}
						loading={loading}
						linhaSelecionada={linhaSelecionada}
						setLinhaSelecionada={setLinhaSelecionada}
						filtroCliente={filtroCliente}
						setFiltroCliente={setFiltroCliente}
						filtroStatus={filtroStatus}
						setFiltroStatus={setFiltroStatus}
						onAtualizarCache={handleAtualizarCache}
						coletando={coletando}
						onTemplateAtualizado={buscarComparativo}
					/>
				)}
				{abaAtiva === 1 && (
					<AbaScripts
						scripts={scripts}
						saudeColeta={saudeColetaScripts}
						loading={loadingScripts}
						linhaSelecionada={linhaSelecionadaScripts}
						setLinhaSelecionada={setLinhaSelecionadaScripts}
						filtroCliente={filtroClienteScripts}
						setFiltroCliente={setFiltroClienteScripts}
						filtroStatus={filtroStatusScripts}
						setFiltroStatus={setFiltroStatusScripts}
						onAtualizarCache={handleAtualizarCache}
						coletando={coletando}
						onScriptAtualizado={buscarComparativoScripts}
					/>
				)}
			</Box>
		</Box>
	);
}
